/**
 * Device Fingerprinting Utility
 * 
 * IMPORTANT: This generates a browser-based device fingerprint.
 * It is NOT a true MAC address - browsers cannot access MAC addresses for security reasons.
 * 
 * This fingerprint is used for:
 * - Security (detect suspicious login patterns)
 * - Fraud prevention (multiple accounts from same device)
 * - Analytics (unique device counting)
 * 
 * Privacy: User consent is required via registration checkbox.
 * Data is encrypted with AES-256 before storage.
 */

/**
 * Generate a unique device fingerprint using various browser characteristics
 * @returns {Promise<string>} SHA-256 hash of device characteristics
 */
export async function generateDeviceFingerprint() {
  const components = {
    canvas: await getCanvasFingerprint(),
    webgl: await getWebGLFingerprint(),
    screen: getScreenFingerprint(),
    timezone: getTimezoneFingerprint(),
    platform: getPlatformFingerprint(),
    plugins: getPluginsFingerprint(),
    fonts: await getFontsFingerprint(),
    audioContext: await getAudioFingerprint(),
  };

  // Combine all components into a single string
  const fingerprintString = JSON.stringify(components);
  
  // Hash the string to create a consistent fingerprint
  const fingerprint = await hashString(fingerprintString);
  
  return fingerprint;
}

/**
 * Generate a canvas fingerprint
 * Different devices render canvas elements slightly differently
 * @returns {Promise<string>} Canvas data hash
 */
async function getCanvasFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 30;
    const ctx = canvas.getContext('2d');
    
    // Draw text with various styles
    ctx.textBaseline = 'top';
    ctx.font = '14px "Arial"';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('VeriPass Fingerprint 🔐', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('VeriPass Fingerprint 🔐', 4, 17);
    
    // Get canvas data
    const dataURL = canvas.toDataURL();
    return await hashString(dataURL);
  } catch (err) {
    console.warn('Canvas fingerprint failed:', err);
    return 'canvas_unavailable';
  }
}

/**
 * Generate a WebGL fingerprint
 * GPU and graphics drivers affect WebGL rendering
 * @returns {Promise<string>} WebGL parameters hash
 */
async function getWebGLFingerprint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) {
      return 'webgl_unavailable';
    }
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = debugInfo 
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    
    const webglData = {
      vendor,
      renderer,
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
    };
    
    return await hashString(JSON.stringify(webglData));
  } catch (err) {
    console.warn('WebGL fingerprint failed:', err);
    return 'webgl_unavailable';
  }
}

/**
 * Get screen characteristics
 * @returns {string} Screen fingerprint
 */
function getScreenFingerprint() {
  return JSON.stringify({
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth,
    devicePixelRatio: window.devicePixelRatio || 1,
  });
}

/**
 * Get timezone information
 * @returns {string} Timezone fingerprint
 */
function getTimezoneFingerprint() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Date().getTimezoneOffset();
  return JSON.stringify({ timezone: tz, offset });
}

/**
 * Get platform and user agent info
 * @returns {string} Platform fingerprint
 */
function getPlatformFingerprint() {
  return JSON.stringify({
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    hardwareConcurrency: navigator.hardwareConcurrency,
    maxTouchPoints: navigator.maxTouchPoints,
    vendor: navigator.vendor,
  });
}

/**
 * Get browser plugins
 * @returns {string} Plugins fingerprint
 */
function getPluginsFingerprint() {
  if (!navigator.plugins || navigator.plugins.length === 0) {
    return 'no_plugins';
  }
  
  const plugins = Array.from(navigator.plugins).map(p => ({
    name: p.name,
    description: p.description,
  }));
  
  return JSON.stringify(plugins);
}

/**
 * Detect available fonts
 * @returns {Promise<string>} Fonts fingerprint
 */
async function getFontsFingerprint() {
  try {
    // List of common fonts to test
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia',
      'Palatino', 'Garamond', 'Comic Sans MS', 'Trebuchet MS', 'Impact',
      'Malgun Gothic', 'Gulim', 'Batang', 'Dotum', 'NanumGothic',
    ];
    
    // Detect which fonts are available
    const availableFonts = testFonts.filter(font => isFontAvailable(font, baseFonts));
    
    return JSON.stringify(availableFonts.sort());
  } catch (err) {
    console.warn('Font fingerprint failed:', err);
    return 'fonts_unavailable';
  }
}

/**
 * Check if a specific font is available
 * @param {string} fontName - Font to check
 * @param {string[]} baseFonts - Base fonts for comparison
 * @returns {boolean} True if font is available
 */
function isFontAvailable(fontName, baseFonts) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const text = 'mmmmmmmmmmlli';
  const fontSize = '72px';
  
  canvas.width = 100;
  canvas.height = 50;
  
  // Test with each base font
  const widths = {};
  for (const baseFont of baseFonts) {
    context.font = `${fontSize} ${baseFont}`;
    widths[baseFont] = context.measureText(text).width;
  }
  
  // Test with target font
  context.font = `${fontSize} ${fontName}, ${baseFonts[0]}`;
  const targetWidth = context.measureText(text).width;
  
  // If target width differs from base font, font is available
  return baseFonts.some(baseFont => targetWidth !== widths[baseFont]);
}

/**
 * Generate audio context fingerprint
 * Audio rendering differs slightly across devices
 * @returns {Promise<string>} Audio fingerprint
 */
async function getAudioFingerprint() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      return 'audio_unavailable';
    }
    
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const analyser = context.createAnalyser();
    const gainNode = context.createGain();
    const scriptProcessor = context.createScriptProcessor(4096, 1, 1);
    
    gainNode.gain.value = 0; // Mute
    oscillator.connect(analyser);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gainNode);
    gainNode.connect(context.destination);
    
    oscillator.start(0);
    
    // Collect audio data
    const audioData = await new Promise((resolve) => {
      scriptProcessor.onaudioprocess = function(event) {
        const output = event.outputBuffer.getChannelData(0);
        const sum = Array.from(output).reduce((a, b) => a + Math.abs(b), 0);
        oscillator.stop();
        scriptProcessor.disconnect();
        resolve(sum);
      };
    });
    
    context.close();
    
    return audioData.toString();
  } catch (err) {
    console.warn('Audio fingerprint failed:', err);
    return 'audio_unavailable';
  }
}

/**
 * Hash a string using SHA-256
 * @param {string} str - String to hash
 * @returns {Promise<string>} Hex-encoded hash
 */
async function hashString(str) {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (err) {
    console.error('Hashing failed:', err);
    // Fallback to simple hash
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }
}

/**
 * Get detailed device metadata (non-hashed)
 * Used for analytics and display in admin panel
 * @returns {Object} Device metadata
 */
export function getDeviceMetadata() {
  const ua = navigator.userAgent;
  
  return {
    userAgent: ua,
    browser: detectBrowser(ua),
    os: detectOS(ua),
    deviceType: detectDeviceType(ua),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

/**
 * Detect browser from user agent
 * @param {string} ua - User agent string
 * @returns {string} Browser name
 */
function detectBrowser(ua) {
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Opera/') || ua.includes('OPR/')) return 'Opera';
  if (ua.includes('MSIE') || ua.includes('Trident/')) return 'Internet Explorer';
  return 'Unknown';
}

/**
 * Detect operating system from user agent
 * @param {string} ua - User agent string
 * @returns {string} OS name
 */
function detectOS(ua) {
  if (ua.includes('Windows NT 10.0')) return 'Windows 10';
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows NT 6.2')) return 'Windows 8';
  if (ua.includes('Windows NT 6.1')) return 'Windows 7';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+)[_.](\d+)/);
    if (match) return `macOS ${match[1]}.${match[2]}`;
    return 'macOS';
  }
  if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    if (match) return `Android ${match[1]}`;
    return 'Android';
  }
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+)_(\d+)/);
    if (match) return `iOS ${match[1]}.${match[2]}`;
    return 'iOS';
  }
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('CrOS')) return 'Chrome OS';
  return 'Unknown';
}

/**
 * Detect device type from user agent
 * @param {string} ua - User agent string
 * @returns {string} Device type
 */
function detectDeviceType(ua) {
  if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
    return 'Mobile';
  }
  if (ua.includes('Tablet') || ua.includes('iPad')) {
    return 'Tablet';
  }
  return 'Desktop';
}

/**
 * Generate a complete device tracking payload
 * Combines fingerprint with metadata
 * @returns {Promise<Object>} Complete device tracking data
 */
export async function generateDeviceTrackingData() {
  const fingerprint = await generateDeviceFingerprint();
  const metadata = getDeviceMetadata();
  
  return {
    deviceFingerprint: fingerprint,
    ...metadata,
  };
}

/**
 * Check if two fingerprints are similar (for fuzzy matching)
 * Handles cases where fingerprint changes slightly due to browser updates
 * @param {string} fp1 - First fingerprint
 * @param {string} fp2 - Second fingerprint
 * @returns {boolean} True if fingerprints are similar enough
 */
export function areFingerprintsSimilar(fp1, fp2) {
  if (fp1 === fp2) return true;
  
  // Compare first 32 characters (most stable part)
  if (fp1.substring(0, 32) === fp2.substring(0, 32)) {
    return true;
  }
  
  // Hamming distance check (count differing characters)
  let differences = 0;
  const minLength = Math.min(fp1.length, fp2.length);
  
  for (let i = 0; i < minLength; i++) {
    if (fp1[i] !== fp2[i]) {
      differences++;
    }
  }
  
  const similarityRatio = 1 - (differences / minLength);
  
  // Consider similar if >80% match
  return similarityRatio >= 0.8;
}

/**
 * Validate tracking consent from user
 * Must be called before generating fingerprint
 * @returns {boolean} True if user has given consent
 */
export function hasTrackingConsent() {
  // Check localStorage for previously given consent
  try {
    const consent = localStorage.getItem('tracking_consent');
    return consent === 'true';
  } catch (err) {
    console.warn('Could not access localStorage:', err);
    return false;
  }
}

/**
 * Save tracking consent to localStorage
 * @param {boolean} consent - User consent status
 */
export function saveTrackingConsent(consent) {
  try {
    localStorage.setItem('tracking_consent', consent.toString());
    localStorage.setItem('tracking_consent_at', new Date().toISOString());
  } catch (err) {
    console.warn('Could not save tracking consent:', err);
  }
}

/**
 * Clear tracking data from localStorage
 * Used when user revokes consent
 */
export function clearTrackingData() {
  try {
    localStorage.removeItem('tracking_consent');
    localStorage.removeItem('tracking_consent_at');
  } catch (err) {
    console.warn('Could not clear tracking data:', err);
  }
}
